import { useTranslation } from '@input-web/configs/locale/locale.config';
import { StandardDetailTabType } from '@input-web/modules/standard/constants/standard.constant';
import { StandardListFilterSchema } from '@input-web/modules/standard/models/standard-list-filter.model';
import standardStore, { useStandardStore } from '@input-web/modules/standard/stores/standard.store';
import standardAdditionalDetailRoute from '@input-web/modules/standard/views/additional-detail/standard-additional-detail.route';
import standardQuestionDetailRoute from '@input-web/modules/standard/views/question-detail/standard-question-detail.route';
import standardScoringDetailRoute from '@input-web/modules/standard/views/scoring-detail/standard-scoring-detail.route';
import { SubjectId } from '@master/constants/subject.constant';
import { useMatch, useNavigate } from '@tanstack/react-router';
import { getValueFromEnum } from '@util/utils/object.util';
import { useMemo } from 'react';

// #region Types
type UseStandardDetailLayoutTabProps = {
  filter?: StandardListFilterSchema;
  partId?: string;
};
// #endregion

// #region Utils
function getStandardDetailPath(tab: StandardDetailTabType) {
  return getValueFromEnum(
    tab,
    {
      [StandardDetailTabType.Additional]: standardAdditionalDetailRoute.fullPath,
      [StandardDetailTabType.Scoring]: standardScoringDetailRoute.fullPath,
      [StandardDetailTabType.Question]: standardQuestionDetailRoute.fullPath,
    },
    standardQuestionDetailRoute.fullPath,
  );
}
// #endregion

export default function useStandardDetailLayoutTab({
  filter,
  partId,
}: UseStandardDetailLayoutTabProps) {
  // #region Dependencies
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isQuestionMatched = !!useMatch({
    from: standardQuestionDetailRoute.id,
    shouldThrow: false,
  });
  const isAdditionalMatched = !!useMatch({
    from: standardAdditionalDetailRoute.id,
    shouldThrow: false,
  });
  const isScoringMatched = !!useMatch({ from: standardScoringDetailRoute.id, shouldThrow: false });
  const { shouldConfirmLeavingForm } = useStandardStore();
  // #endregion

  // #region Computed Values
  const items = useMemo(() => {
    const baseAdditionalKey = getValueFromEnum(filter?.subjectId, {
      [SubjectId.English]: 'english' as const,
      [SubjectId.Japanese]: 'japanese' as const,
    });
    const additionalKey = baseAdditionalKey ? (`.${baseAdditionalKey}` as const) : '';

    return [
      StandardDetailTabType.Question,
      ...getValueFromEnum(
        filter?.subjectId,
        {
          [SubjectId.English]: [],
          [SubjectId.Japanese]: [StandardDetailTabType.Scoring],
        },
        [],
      ),
      StandardDetailTabType.Additional,
    ].map((tab) => ({
      active:
        (isQuestionMatched && tab === StandardDetailTabType.Question) ||
        (isAdditionalMatched && tab === StandardDetailTabType.Additional) ||
        (isScoringMatched && tab === StandardDetailTabType.Scoring),
      description: t(
        tab === StandardDetailTabType.Additional
          ? `standard.detail-layout.tab.description.additional${additionalKey}`
          : `standard.detail-layout.tab.description.${tab}`,
      ) as string,
      onClick: () => {
        const onNavigate = () => {
          navigate({
            params: { partId: partId ?? '' },
            search: true,
            state: true,
            to: getStandardDetailPath(tab),
          });
        };

        if (shouldConfirmLeavingForm) {
          standardStore.actions.openConfirmationDialog({ onConfirm: onNavigate });
          return;
        }

        onNavigate();
      },
      title: t(`standard.detail-layout.tab.title.${tab}`) as string,
    }));
  }, [
    isQuestionMatched,
    isAdditionalMatched,
    isScoringMatched,
    shouldConfirmLeavingForm,
    t,
    navigate,
    partId,
    filter?.subjectId,
  ]);
  // #endregion

  // #region Computed Values
  const description = useMemo(() => items.find(({ active }) => active)?.description, [items]);
  // #endregion

  return { description, items };
}
